import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  ContractorDto,
  ContractorPaymentTypeEnum,
  ContractorRegionDto,
  ContractorUserDto,
  RegionDto,
  UserDto,
} from '@vatos-pas/common';
import { AppRole } from 'providers/roles';
import { extractErrorMessage } from 'utils';

export interface RegionOption {
  id: string;
  name: string;
}

export interface BumperOption {
  id: string;
  name: string;
}

export interface ContractorFormValues {
  name: string;
  corporation: string;
  paymentType: ContractorPaymentTypeEnum | '';
  paymentRate: number | null;
  bumperPaymentType: ContractorPaymentTypeEnum | '';
  bumperPaymentRate: number | null;
  hangerAvailable: boolean;
  finisherAvailable: boolean;
  sprayerAvailable: boolean;
  trimAvailable: boolean;
  paintAvailable: boolean;
  bumperAvailable: boolean;
  specialistAvailable: boolean;
  hangerCapacity: number | null;
  finisherCapacity: number | null;
  sprayerCapacity: number | null;
  trimCapacity: number | null;
  paintCapacity: number | null;
  bumperCapacity: number | null;
  active: boolean;
  suspended: boolean;
  canPerformHighCeilingTasks: boolean;
  notes: string;
  selectedRegions: string[];
  selectedBumpers: string[];
}

export type ContractorMutationPayload = Partial<
  Omit<ContractorDto, 'id' | 'createdAt' | 'updatedAt'>
>;

function useContractor() {
  const dataProvider = useDataProvider();

  const [contractorId, setContractorId] = useState<string>('');
  const {
    data: selectedContractor,
    loading: contractorLoading,
    loaded: contractorLoaded,
    error: contractorError,
  } = useGetOne<ContractorDto>('contractor', contractorId, {
    enabled: !!contractorId,
  });

  const isLoading = !!contractorId && !selectedContractor && contractorLoading;

  const [formLoading, setFormLoading] = useState(false);
  const [contractorRegions, setContractorRegions] = useState<
    ContractorRegionDto[]
  >([]);
  const [contractorUsers, setContractorUsers] = useState<ContractorUserDto[]>(
    [],
  );
  const [contractorRegionsLoaded, setContractorRegionsLoaded] = useState(false);
  const [contractorUsersLoaded, setContractorUsersLoaded] = useState(false);
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [bumpersError, setBumpersError] = useState<string | null>(null);

  const loadContractorById = useCallback((id: string): void => {
    setContractorId(id);
  }, []);

  const createContractor = useCallback(
    async (data: ContractorMutationPayload): Promise<ContractorDto> => {
      const result = await dataProvider.create<ContractorDto>('contractor', {
        data,
      });
      const contractor = result.data;
      setContractorId(contractor.id);
      return contractor;
    },
    [dataProvider],
  );

  const updateContractor = useCallback(
    async (
      id: string,
      data: ContractorMutationPayload,
    ): Promise<ContractorDto> => {
      if (!selectedContractor) {
        throw new Error('Cannot update: contractor data not loaded');
      }
      const result = await dataProvider.update<ContractorDto>('contractor', {
        id,
        data: { ...selectedContractor, ...data },
        previousData: selectedContractor,
      });
      return result.data;
    },
    [dataProvider, selectedContractor],
  );

  const loadRegionOptions = useCallback(
    async (_searchTerm: string): Promise<RegionOption[]> => {
      try {
        const result = await dataProvider.getList<RegionDto>('region', {
          filter: {},
          pagination: { page: 1, perPage: 999 },
          sort: { field: 'name', order: 'ASC' },
        });
        setRegionsError(null);
        return result.data.map(region => ({
          id: region.id,
          name: region.name,
        }));
      } catch (error) {
        console.error('Failed to load regions:', error);
        setRegionsError(extractErrorMessage(error, 'Failed to load regions'));
        return [];
      }
    },
    [dataProvider],
  );

  const loadBumperOptions = useCallback(
    async (contractorId: string): Promise<BumperOption[]> => {
      try {
        const result = await dataProvider.getList<UserDto>('user', {
          filter: {
            s: {
              $and: [
                { active: true },
                {
                  $or: [
                    { 'contractorUser.contractorId': { $isnull: true } },
                    { 'contractorUser.contractorId': { $eq: contractorId } },
                  ],
                },
                { 'userRoles.role.name': { $eq: AppRole.Bumper } },
              ],
            },
          },
          pagination: { page: 1, perPage: 200 },
          sort: { field: 'firstName', order: 'ASC' },
        });
        setBumpersError(null);
        return result.data.map(user => ({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
        }));
      } catch (error) {
        console.error('Failed to load bumpers:', error);
        setBumpersError(extractErrorMessage(error, 'Failed to load bumpers'));
        return [];
      }
    },
    [dataProvider],
  );

  const loadContractorRegionsForContractor = useCallback(
    async (contractorId: string): Promise<ContractorRegionDto[]> => {
      try {
        const result = await dataProvider.getList<ContractorRegionDto>(
          'contractor-region',
          {
            filter: { contractorId },
            pagination: { page: 1, perPage: 80 },
            sort: { field: 'id', order: 'DESC' },
          },
        );
        setContractorRegions(result.data);
        setContractorRegionsLoaded(true);
        setRegionsError(null);
        return result.data;
      } catch (error) {
        console.error('Failed to load contractor regions:', error);
        setContractorRegions([]);
        setContractorRegionsLoaded(true);
        setRegionsError(
          extractErrorMessage(error, 'Failed to load contractor regions'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  const loadContractorUsersForContractor = useCallback(
    async (contractorId: string): Promise<ContractorUserDto[]> => {
      try {
        const result = await dataProvider.getList<ContractorUserDto>(
          'contractor-user',
          {
            filter: { contractorId },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'id', order: 'ASC' },
          },
        );
        setContractorUsers(result.data);
        setContractorUsersLoaded(true);
        setBumpersError(null);
        return result.data;
      } catch (error) {
        console.error('Failed to load contractor users:', error);
        setContractorUsers([]);
        setContractorUsersLoaded(true);
        setBumpersError(
          extractErrorMessage(error, 'Failed to load contractor bumpers'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  const createContractorRegion = useCallback(
    async (contractorId: string, regionId: string): Promise<void> => {
      await dataProvider.create('contractor-region', {
        data: { contractorId, regionId },
      });
    },
    [dataProvider],
  );

  const deleteContractorRegion = useCallback(
    async (contractorRegionId: string): Promise<void> => {
      await dataProvider.delete('contractor-region', {
        id: contractorRegionId,
        previousData: { id: contractorRegionId },
      });
    },
    [dataProvider],
  );

  const batchUpdateContractorRegions = useCallback(
    async (
      contractorId: string,
      existingContractorRegions: ContractorRegionDto[],
      newRegionIds: string[],
    ): Promise<void> => {
      const existingRegionIds = existingContractorRegions.map(
        cr => cr.regionId,
      );
      const regionsToAdd = newRegionIds.filter(
        id => !existingRegionIds.includes(id),
      );
      const regionsToDelete = existingContractorRegions.filter(
        cr => !newRegionIds.includes(cr.regionId),
      );

      // Additions first: the API refuses a Regional Admin removing a Contractor's last Region.
      await Promise.all(
        regionsToAdd.map(regionId =>
          createContractorRegion(contractorId, regionId),
        ),
      );
      await Promise.all(regionsToDelete.map(cr => deleteContractorRegion(cr.id)));
    },
    [createContractorRegion, deleteContractorRegion],
  );

  const batchUpdateContractorUsers = useCallback(
    async (
      contractorId: string,
      existingContractorUsers: ContractorUserDto[],
      newBumperUserIds: string[],
    ): Promise<void> => {
      const deleteRowIds = existingContractorUsers
        .filter(cu => !newBumperUserIds.includes(cu.userId))
        .map(cu => cu.id);
      const existingUserIds = existingContractorUsers.map(cu => cu.userId);
      const insertUserIds = newBumperUserIds.filter(
        userId => !existingUserIds.includes(userId),
      );

      if (!deleteRowIds.length && !insertUserIds.length) return;

      await dataProvider.create('contractor-user/batch-process', {
        data: {
          delete: deleteRowIds,
          insert: insertUserIds.map(userId => ({ contractorId, userId })),
        },
      });
    },
    [dataProvider],
  );

  const resetLoadedFlags = useCallback(() => {
    setContractorRegionsLoaded(false);
    setContractorUsersLoaded(false);
  }, []);

  return {
    selectedContractor,
    isLoading,
    contractorLoaded,
    contractorError,
    formLoading,
    contractorRegions,
    contractorUsers,
    contractorRegionsLoaded,
    contractorUsersLoaded,
    regionsError,
    bumpersError,
    loadContractorById,
    createContractor,
    updateContractor,
    loadRegionOptions,
    loadBumperOptions,
    loadContractorRegionsForContractor,
    loadContractorUsersForContractor,
    batchUpdateContractorRegions,
    batchUpdateContractorUsers,
    setFormLoading,
    resetLoadedFlags,
  };
}

export const ContractorContainer = createContainer(useContractor);
